import React, { ReactNode } from 'react';

import AutoComplete, {
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';

import CustomTextField from '../CustomTextField';
import CustomPopper from '../CustomPopper';
import { Box, Chip } from '@material-ui/core';
import useStyles from './styles';

export interface CustomAutoCompleteProps<
  T extends { label?: string; value?: any }
> {
  options: T[];
  getOptionLabel?: (option: T) => string;
  getOptionSelected?: (option: T, value: T) => boolean;
  optionValue?: T;
  inputValue?: string;
  label?: string;
  loading?: boolean;
  disableClearable?: boolean;
  clearOnBlur?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  multiple?: boolean;
  defaultValue?: any;
  groupBy?: (option: T) => string;
  onChange: (value: string) => void;
  onAutoCompleteChange: (option: any) => void;
  onBlur?: () => void;
  onOpen?: () => void;
  renderGroup?: (option: any) => ReactNode;
}

function CustomAutoComplete<T extends { label: string; value: any }>(
  props: CustomAutoCompleteProps<T>,
) {
  const classes = useStyles();

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <CustomTextField
      {...params}
      error={props?.error}
      helperText={props?.helperText}
      label={props?.label}
      value={props?.inputValue}
      onChange={(e) => props?.onChange(e?.target?.value)}
      required={props?.required}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <React.Fragment>
            {props?.loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : null}
            {params.InputProps.endAdornment}
          </React.Fragment>
        ),
      }}
    />
  );
  const autoCompleteProps: AutocompleteProps<T, any, any, any> = {
    groupBy: props?.groupBy,
    multiple: props?.multiple,
    disabled: props?.disabled,
    clearOnBlur: props?.clearOnBlur,
    noOptionsText: 'Aucun résultat',
    disableClearable: props?.disableClearable,
    onChange: (e: any, value: any) => {
      props?.onAutoCompleteChange(value);
    },
    onBlur: props?.onBlur,
    onOpen: props?.onOpen,
    loading: props?.loading,
    options: props?.options,
    getOptionLabel:
      props?.getOptionLabel || ((option: T) => option?.label || ''),
    ...(props?.getOptionSelected && {getOptionSelected: props?.getOptionSelected}),
    PopperComponent: CustomPopper,
    defaultValue: props?.defaultValue,
    renderGroup: props?.renderGroup,
    renderInput,
    renderTags: (values, _) => {
      const numTags = values?.length ?? 0;

      return (
        <Box className={classes.root}>
          {values.slice(0, 2).map((option, index) => (
            <Chip
              key={`autocomplete-tags-${index}`}
              label={
                (props?.getOptionLabel && props?.getOptionLabel(option)) ??
                option?.label ??
                ''
              }
            />
          ))}

          {numTags > 2 && <Chip disabled label={`+${numTags - 2}`} />}
        </Box>
      );
    },
  };
  if (props?.optionValue !== undefined) {
    return <AutoComplete {...autoCompleteProps} value={props?.optionValue} />;
  }
  return <AutoComplete {...autoCompleteProps} />;
}

export default CustomAutoComplete;
